import { useEffect, useState } from 'react';

import { getHeadingIdsToElements } from '@confluence/editor-heading-links/entry-points/headingLinksFromElements.ts';

import {
	addRafThrottledScrollListener,
	getScrollSelectedHeadingId,
	resolveHeadings,
} from './scrollSelection.ts';
import type { TableOfContentsItem } from './tableOfContentsTypes.ts';

const EDITOR_SCROLL_CONTAINER_SELECTOR = '[data-editor-scroll-container="true"]';

export function useEditorScrollSelection(
	items: TableOfContentsItem[],
	isEnabled: boolean,
): string | undefined {
	const [selectedHeadingId, setSelectedHeadingId] = useState<string>();

	useEffect(() => {
		if (!isEnabled || items.length === 0) {
			setSelectedHeadingId(undefined);
			return;
		}

		const headingElements = getHeadingIdsToElements();
		const resolvedHeadings = resolveHeadings(items, (headingId) => headingElements.get(headingId));
		const scrollContainer = resolvedHeadings[0]?.element.closest(EDITOR_SCROLL_CONTAINER_SELECTOR);
		const ownerWindow = scrollContainer?.ownerDocument.defaultView;
		if (!scrollContainer || !ownerWindow) {
			setSelectedHeadingId(undefined);
			return;
		}

		const updateSelectedHeading = () => {
			const nextSelectedHeadingId = getScrollSelectedHeadingId(
				resolvedHeadings,
				scrollContainer.getBoundingClientRect().top,
			);
			setSelectedHeadingId((currentSelectedHeadingId) =>
				currentSelectedHeadingId === nextSelectedHeadingId
					? currentSelectedHeadingId
					: nextSelectedHeadingId,
			);
		};

		updateSelectedHeading();
		return addRafThrottledScrollListener(scrollContainer, ownerWindow, updateSelectedHeading);
	}, [isEnabled, items]);

	return selectedHeadingId;
}
